import { useCallback, useEffect, useState } from 'react'
import config from 'src/config'
import { post } from 'src/functions/fetch'
import { ProductGuaranteeInfo, productGuaranteesResponseSchema } from 'src/shared/manufacturerGuarantees'

type ItemReference = { id: number; key: string }
type Result = { signature: string; items: ProductGuaranteeInfo[]; error: boolean }

export const loadManufacturerGuarantees = async (items: ItemReference[], signal?: AbortSignal) => {
  if (!items.length) return []
  const response = await post({
    url: 'products/guarantees',
    body: { shop: config.shop.shop, items },
    signal,
  })
  const parsed = productGuaranteesResponseSchema.safeParse(response)
  if (!parsed.success) throw new Error('Garantieinformationen konnten nicht geladen werden.')
  const expected = new Set(items.map(item => `${item.id}:${item.key}`))
  const received = new Set(parsed.data.items.map(item => `${item.id}:${item.key}`))
  if (expected.size !== received.size || [...expected].some(key => !received.has(key))) {
    throw new Error('Die Garantieinformationen passen nicht zum aktuellen Warenkorb.')
  }
  return parsed.data.items
}

/** Re-read central manufacturer data; never use guarantees stored in the browser cart. */
export const useManufacturerGuarantees = (references: ItemReference[]) => {
  const signature = JSON.stringify(references.map(({ id, key }) => ({ id, key })))
  const [result, setResult] = useState<Result | null>(null)
  const [refreshVersion, setRefreshVersion] = useState(0)
  const refresh = useCallback(() => setRefreshVersion(version => version + 1), [])

  useEffect(() => {
    const items = JSON.parse(signature) as ItemReference[]
    const controller = new AbortController()
    let sequence = 0
    const update = async () => {
      const request = ++sequence
      try {
        const data = await loadManufacturerGuarantees(items, controller.signal)
        if (!controller.signal.aborted && request === sequence) setResult({ signature, items: data, error: false })
      } catch {
        if (!controller.signal.aborted && request === sequence) setResult({ signature, items: [], error: true })
      }
    }
    void update()
    const onFocus = () => { void update() }
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void update()
    }, 60000)
    window.addEventListener('focus', onFocus)
    return () => {
      controller.abort()
      window.clearInterval(timer)
      window.removeEventListener('focus', onFocus)
    }
  }, [signature, refreshVersion])

  const current = result?.signature === signature ? result : null
  return {
    items: current?.items,
    ready: references.length === 0 || Boolean(current && !current.error),
    loading: references.length > 0 && !current,
    error: Boolean(current?.error),
    refresh,
  }
}
